import _ from "lodash";
import update from "immutability-helper";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import client from "lib/apolloClient/client";
import { pypElementMapping } from "Constants/plannerElementMappings";
import {
  getStudentsPypElementRatingsQuery,
  getClassProgressSummaryCsvExportQuery,
} from "ClassProgressSummary/modules/ClassProgressSummaryQuery";
import { exportData } from "Utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  createIBPYPElementRatingsMutation,
  deleteIBPYPElementRatingByIdMutation,
} from "modules/CommonMutations";
import {
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_SUBJECT,
  CURRICULUM_TYPE_UBD,
} from "Constants/stringConstants";
import { getStudentsPypElementRatingHistoryQuery } from "./ClassProgressSummaryQuery";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

export const NAME = "classProgressSummary";

export const UPDATE_FILTERS = "UPDATE_FILTERS" + " " + NAME;

export const INIT_FILTERS = "INIT_FILTERS" + " " + NAME;

export const updateFilters = data => {
  return {
    type: UPDATE_FILTERS,
    data,
  };
};

/**
 * This function process the class progress summary and downloads the
 * whole data as csv when triggered by the user.
 */
export const downloadClassProgressSummary = () => {
  // write code to download csv file for the summary
  return async (dispatch, getState) => {
    const courseId = getState().teacher.selected_class.selected_course;
    const academicYearId = getSelectedAcademicYearId(getState());
    const { title } = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters: { academicYearId },
    });
    const courseTitle = _.join(_.split(title, " "), "_");

    try {
      await client
        .query({
          query: getClassProgressSummaryCsvExportQuery,
          variables: {
            courseId,
          },
          fetchPolicy: "network-only",
        })
        .then(
          ({
            data: {
              node: { classProgressSummaryCsvExport },
            },
          }) => {
            exportData({
              fileName: `ClassProgressSummaryCSV_${courseTitle}`,
              outputFormat: "CSV",
              data: classProgressSummaryCsvExport,
            });
          }
        );
    } catch {}
  };
};

export const getElementTypesForEvaluation = ({ elementType }) => {
  switch (elementType) {
    case ELEMENT_TYPE_UBD_SUBJECT:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      return [ELEMENT_TYPE_UBD_LEARNING_STANDARD, ELEMENT_TYPE_UBD_SUBJECT];
    default:
      return elementType;
  }
};

export const createPypElementRatings = ({
  localElementsRatings,
  curriculumType,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo();
    const userId = userInfo.id;
    const filters = getState().classProgressSummary.filters;
    const studentIds = filters["students"];
    const subjectIds = filters["subjects"];
    const academicCriteriaSetId = filters["criteriaSet"];
    const [elementId] = filters["pypElement"].split(":");
    const elementType = pypElementMapping[elementId];
    const elementRatings = _.map(localElementsRatings, rating => {
      return {
        ...rating,
        createdBy: userId,
        parentType: "OBSERVATION",
        isPublished: true,
      };
    });
    const endDate = _.get(
      getState(),
      "platform.academicYearSelected.endDate",
      ""
    );

    try {
      await client.mutate({
        mutation: createIBPYPElementRatingsMutation,
        variables: {
          elementRatings,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentsPypElementRatingsQuery,
            variables: {
              studentIds,
              filtersForRecentMost: {
                academicCriteriaSetId,
                elementTypes: getElementTypesForEvaluation({ elementType }),
                subjectIds:
                  curriculumType == CURRICULUM_TYPE_UBD ? [] : subjectIds,
              },
            },
          },
        ],
      });
    } catch (error) {
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg({ msg: error.message, notString: true }));
      }
    }
  };
};

const REDUCER_HANDLERS = {
  [INIT_FILTERS]: (state, action) => {
    return update(state, {
      filters: { $set: initialState.filters },
    });
  },
  [UPDATE_FILTERS]: (state, action) => {
    const params = action.data;
    Object.keys(params).map((key, index) => {
      state = update(state, {
        filters: { [key]: { $set: params[key] } },
      });
    });
    return state;
  },
};

const initialState = {
  filters: {
    students: [],
    pypElement: "",
    criteriaSet: "",
    searchText: "",
    grades: [],
    subjects: [],
    units: [],
  },
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

/**
 * This function converts rating data to element-wise rating data.
 * Now searching for any student rating is an constant time operation.
 *
 * @param {Array} elementsRating
 * @returns {Object} - normalized Object rating data
 */
export const transformPypElementRatingData = ({ elementsRating }) => {
  return _.reduce(
    elementsRating,
    (accumulator, item) => {
      if (!accumulator[item.ibPYPElementId]) {
        accumulator[item.ibPYPElementId] = {};
      }
      accumulator[item.ibPYPElementId][item.studentId] = {
        academicCriteriaSetId: item.academicCriteriaSetId,
        ibPYPElementType: item.ibPYPElementType,
        academicCriteriaValueId: item.academicCriteriaValueId,
      };

      return accumulator;
    },
    {}
  );
};

export const deleteIBPYPElementRatingById = ({
  id,
  queryVariables,
  curriculumType,
}) => {
  return async (dispatch, getState) => {
    const filters = getState().classProgressSummary.filters;
    const studentIds = filters["students"];
    const academicCriteriaSetId = filters["criteriaSet"];
    try {
      await client.mutate({
        mutation: deleteIBPYPElementRatingByIdMutation,
        variables: {
          id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getStudentsPypElementRatingHistoryQuery,
            variables: {
              studentId: queryVariables.studentId,
              endDate: queryVariables.endDate,
              filtersForRatingHistory: {
                ibPYPElementId: queryVariables.ibPYPElementId,
                ibPYPElementType: queryVariables.ibPYPElementType,
                last: 30,
                academicCriteriaSetId: queryVariables.academicCriteriaSetId,
                subjectIds: queryVariables.subjectIds,
              },
            },
          },
          {
            query: getStudentsPypElementRatingsQuery,
            variables: {
              studentIds,
              filtersForRecentMost: {
                academicCriteriaSetId: academicCriteriaSetId,
                elementTypes: getElementTypesForEvaluation({
                  elementType: queryVariables.ibPYPElementType,
                }),
                subjectIds:
                  curriculumType == CURRICULUM_TYPE_UBD
                    ? []
                    : queryVariables.subjectIds,
              },
            },
          },
        ],
      });
    } catch (error) {
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(
          setToastMsg({
            msg: "toastMsgs:something_went_wrong",
            notString: true,
          })
        );
      }
    }
  };
};
